import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ChevronDown, Globe } from "lucide-react";
import TimeSlots from "./timeslots";
import {
  dayNames,
  formatDateLocal,
  parseLocalDate,
  monthNames,
} from "./resource";
import TimezoneSelector from "./timezoneselector";

// Helper function to get start of week (Monday)
const getWeekStart = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
  return new Date(d.setDate(diff));
};

// Helper function to get end of week (Sunday)
const getWeekEnd = (date) => {
  const weekStart = getWeekStart(date);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  return weekEnd;
};

const Calendar = (props) => {
  const { available_slots, preselectedTimeZone, onShowDemoForm } = props;

  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState();

  const timeSlotsRef = useRef(null);

  const [selectedTimezone, setSelectedTimezone] = useState(preselectedTimeZone);

  const [timeSlots, setTimeSlots] = useState([]);

  const [allTimeSlots, setAllTimeSlots] = useState([]);

  const [availableDates, setAvailableDates] = useState([]);

  const [currentWeekStart, setCurrentWeekStart] = useState(new Date());

  const [selectedTime, setSelectedTime] = useState();

  // Initialize current week start
  useEffect(() => {
    const today = new Date();
    setCurrentWeekStart(getWeekStart(today));
  }, []);

  useEffect(() => {
    if (!available_slots || available_slots.length === 0) return;

    const datesSet = new Set();
    const slotMap = {};
    let firstAvailableDate = null;
    const dateInfoMap = {};

    const filteredAvailableSlots = available_slots.filter((slot) => {
      const viewerStart = slot?.viewer_timezone?.start;
      if (!viewerStart) return false;

      const slotDate = new Date(viewerStart);
      const slotHour = slotDate.getHours();

      // Exclude slots between 12:00 AM and 7:59 AM
      return slotHour >= 8;
    });

    filteredAvailableSlots?.forEach((slot) => {
      const slotDate = new Date(slot.viewer_timezone.start);
      const dateStr = formatDateLocal(slotDate);

      if (
        !firstAvailableDate ||
        parseLocalDate(dateStr) < parseLocalDate(firstAvailableDate)
      ) {
        firstAvailableDate = dateStr;
      }

      datesSet.add(dateStr);

      // Store date info for display
      if (!dateInfoMap[dateStr]) {
        const normalizedDate = new Date(
          slotDate.getFullYear(),
          slotDate.getMonth(),
          slotDate.getDate()
        );
        dateInfoMap[dateStr] = {
          dateStr,
          date: normalizedDate,
          day: slotDate.getDate(),
          month: slotDate.getMonth(),
          year: slotDate.getFullYear(),
          dayName: slotDate.toLocaleDateString("en-US", { weekday: "short" }),
          monthName: slotDate.toLocaleDateString("en-US", { month: "short" }),
        };
      }

      if (!slotMap[dateStr]) {
        slotMap[dateStr] = [];
      }

      slotMap[dateStr].push({
        start: slot.viewer_timezone.start,
        end: slot.viewer_timezone.end,
        display_start: slot.viewer_timezone.display_start,
        display_end: slot.viewer_timezone.display_end,
        start_iso: slot.viewer_timezone.start_iso,
        end_iso: slot.viewer_timezone.end_iso,
        timezone: slot.viewer_timezone.timezone,
      });
    });

    const sortedDates = Array.from(datesSet).sort();
    const dateObjects = sortedDates
      .map((dateStr) => dateInfoMap[dateStr])
      .filter(Boolean);

    setAvailableDates(dateObjects);
    setAllTimeSlots(slotMap);
    // setCurrentPage(0); // Reset to first page when dates change
  }, [available_slots, currentDate]);

  useEffect(() => {
    if (
      Object.keys(allTimeSlots).length > 0 &&
      selectedDate &&
      allTimeSlots[selectedDate]
    ) {
      const slots = allTimeSlots[selectedDate].map((slot) =>
        slot.display_start.split(" ").slice(-2).join(" ")
      );
      setTimeSlots(slots);
    } else {
      setTimeSlots([]);
    }
  }, [selectedDate, allTimeSlots]);

  useEffect(() => {
    if (selectedTimezone) {
      props.onSelectedTimezone(selectedTimezone);
    }
  }, [selectedTimezone]);

  const calendarData = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);

    const dayOfWeek = firstDay.getDay();
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    startDate.setDate(startDate.getDate() - daysToSubtract);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const days = [];
    const current = new Date(startDate);

    for (let i = 0; i < 42; i++) {
      const dateStr = formatDateLocal(current); // 🔁 new helper
      const dateOnly = new Date(
        current.getFullYear(),
        current.getMonth(),
        current.getDate()
      );
      const isCurrentMonth = current.getMonth() === month;
      const isPast = dateOnly < today;
      const isAvailable = availableDates.includes(dateStr) && !isPast;

      days.push({
        date: new Date(current),
        dateStr,
        day: current.getDate(),
        isCurrentMonth,
        isAvailable,
        isPast,
      });

      current.setDate(current.getDate() + 1);
    }

    return days;
  }, [currentDate, availableDates]);

  // Generate all 7 days of the current week with availability info
  const getCurrentWeekDays = useMemo(() => {
    const weekStart = getWeekStart(currentWeekStart);
    const days = [];

    for (let i = 0; i < 7; i++) {
      const currentDay = new Date(weekStart);
      currentDay.setDate(weekStart.getDate() + i);

      const dateStr = formatDateLocal(currentDay);
      const availableDateInfo = availableDates.find(
        (d) => d.dateStr === dateStr
      );

      days.push({
        date: currentDay,
        dateStr: dateStr,
        day: currentDay.getDate(),
        dayName: currentDay.toLocaleDateString("en-US", { weekday: "short" }),
        monthName: currentDay.toLocaleDateString("en-US", { month: "short" }),
        isAvailable: !!availableDateInfo,
        availableInfo: availableDateInfo,
      });
    }

    return days;
  }, [currentWeekStart, availableDates]);

  // Check if there are dates in previous/next weeks
  const hasNextWeek = useMemo(() => {
    const nextWeekStart = new Date(currentWeekStart);
    nextWeekStart.setDate(nextWeekStart.getDate() + 7);
    const nextWeekEnd = getWeekEnd(nextWeekStart);

    return availableDates.some((dateInfo) => {
      const date = dateInfo.date;
      return date >= nextWeekStart && date <= nextWeekEnd;
    });
  }, [availableDates, currentWeekStart]);

  const hasPrevWeek = useMemo(() => {
    const prevWeekStart = new Date(currentWeekStart);
    prevWeekStart.setDate(prevWeekStart.getDate() - 7);
    const prevWeekEnd = getWeekEnd(prevWeekStart);

    return availableDates.some((dateInfo) => {
      const date = dateInfo.date;
      return date >= prevWeekStart && date <= prevWeekEnd;
    });
  }, [availableDates, currentWeekStart]);

  // Select the first available date from the available slots
  useEffect(() => {
    if (
      !allTimeSlots ||
      typeof allTimeSlots !== "object" ||
      Object.keys(allTimeSlots).length === 0 ||
      !getCurrentWeekDays?.length
    )
      return;

    const firstAvailableDate = Object.keys(allTimeSlots).find(
      (key) => Array.isArray(allTimeSlots[key]) && allTimeSlots[key].length > 0
    );

    if (!firstAvailableDate) return;

    const selectedDayInfo = getCurrentWeekDays.find(
      (day) => day.dateStr === firstAvailableDate
    );

    if (selectedDayInfo) {
      handleDateSelect(selectedDayInfo);
    } else if (firstAvailableDate === availableDates[0]?.dateStr) {
      //  Check if it's exactly 7 days ahead of currentWeekStart so select next week's slot.
      const diffInDays = Math.floor(
        (Date.parse(firstAvailableDate) -
          Date.parse(currentWeekStart.toDateString())) /
          (1000 * 60 * 60 * 24)
      );

      if (diffInDays === 7) {
        const newWeekStart = new Date(currentWeekStart);
        newWeekStart.setDate(newWeekStart.getDate() + 7);
        setCurrentWeekStart(newWeekStart);
      }
    }
  }, [getCurrentWeekDays, allTimeSlots]);

  const navigateWeek = (direction) => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(newWeekStart.getDate() + direction * 7);
    setCurrentWeekStart(newWeekStart);
  };

  // Get week range display
  const getWeekRangeDisplay = () => {
    const weekStart = getWeekStart(currentWeekStart);
    const weekEnd = getWeekEnd(currentWeekStart);

    const startMonth = weekStart.toLocaleDateString("en-US", {
      month: "short",
    });
    const endMonth = weekEnd.toLocaleDateString("en-US", { month: "short" });
    const startDay = weekStart.getDate();
    const endDay = weekEnd.getDate();

    if (startMonth === endMonth) {
      return `${startMonth} ${startDay} - ${endDay}`;
    } else {
      return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
    }
  };

  const handleDateSelect = (dayInfo) => {
    if (!dayInfo.isAvailable) return;
    setSelectedDate(dayInfo.dateStr);
    // setTimeSlots([]);
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    return compareDate.getTime() === today.getTime();
  };

  const handleNext = () => {
    const slots = allTimeSlots[selectedDate];

    const actualtime = slots.find((slot) =>
      slot.display_start.trim().endsWith(selectedTime.trim())
    );

    onShowDemoForm(actualtime);
  };

  return (
    <div className="bg-white pt-4 h-full">
      <div className="flex flex-col max-w-full mx-auto justify-between h-full">
        <div className="justify-start">
          {/* Available Dates Section */}
          <div className="w-full mb-6">
            {/* Timezone Selector */}
            <div className="mb-6">
              <div className="relative">
                <TimezoneSelector
                  defaultTimeZone={selectedTimezone}
                  onSelectTimeZone={(timeZone) => {
                    setSelectedTimezone(timeZone);
                    setAllTimeSlots([]);
                    setAvailableDates([]);
                    setSelectedDate(null);
                    setCurrentWeekStart(getWeekStart(new Date()));
                  }}
                />
              </div>
            </div>

            {/* Available Dates Header with Navigation */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Available Dates
              </h3>
              <div className="flex items-center space-x-3">
                {/* Previous Week Button */}
                <button
                  onClick={() => navigateWeek(-1)}
                  disabled={!hasPrevWeek}
                  className={`p-1.5 rounded-full transition-all ${
                    hasPrevWeek
                      ? "hover:bg-gray-100 text-gray-600"
                      : "text-gray-300 cursor-not-allowed"
                  }`}
                >
                  <ChevronLeft size={18} />
                </button>

                <div className="text-sm text-gray-500 min-w-[100px] text-center">
                  Week of {getWeekRangeDisplay()}
                </div>

                {/* Next Week Button */}
                <button
                  onClick={() => navigateWeek(1)}
                  disabled={!hasNextWeek}
                  className={`p-1.5 rounded-full transition-all ${
                    hasNextWeek
                      ? "hover:bg-gray-100 text-gray-600"
                      : "text-gray-300 cursor-not-allowed"
                  }`}
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>

            {/* Calendar Layout - Weekday Headers */}
            <div className="mb-2">
              <div className="grid grid-cols-7 gap-2">
                {dayNames.map((dayName) => (
                  <div
                    key={dayName}
                    className="text-center text-xs font-medium text-gray-500 py-2"
                  >
                    {dayName}
                  </div>
                ))}
              </div>
            </div>

            {/* Calendar Layout - Date Buttons */}
            <div className="grid grid-cols-7 gap-2">
              {getCurrentWeekDays.map((dayInfo, index) => (
                <button
                  key={`${dayInfo.dateStr}-${index}`}
                  onClick={() => handleDateSelect(dayInfo)}
                  disabled={!dayInfo.isAvailable}
                  className={`flex flex-col items-center justify-center py-3 px-2 rounded-xl border-2 transition-all min-h-[70px] relative ${
                    selectedDate === dayInfo.dateStr && dayInfo.isAvailable
                      ? "bg-blue-600 text-white border-blue-600"
                      : dayInfo.isAvailable
                      ? "bg-white text-gray-700 border-gray-200 hover:border-gray-300 cursor-pointer"
                      : "bg-gray-50 text-gray-400 border-gray-100 cursor-not-allowed"
                  }`}
                >
                  <span
                    className={`text-lg font-bold mb-1 ${
                      selectedDate === dayInfo.dateStr && dayInfo.isAvailable
                        ? "text-white"
                        : dayInfo.isAvailable
                        ? "text-gray-900"
                        : "text-gray-400"
                    }`}
                  >
                    {dayInfo.day}
                  </span>
                  <span
                    className={`text-xs ${
                      selectedDate === dayInfo.dateStr && dayInfo.isAvailable
                        ? "text-white"
                        : dayInfo.isAvailable
                        ? "text-gray-500"
                        : "text-gray-400"
                    }`}
                  >
                    {dayInfo.monthName}
                  </span>

                  {/* By default select the first day. */}
                  {isToday(dayInfo.date) &&
                    selectedDate !== dayInfo.dateStr &&
                    dayInfo.isAvailable && (
                      <div className="absolute bottom-[2px] w-2 h-2 bg-blue-600 rounded-full"></div>
                    )}
                </button>
              ))}
            </div>
          </div>

          {/* Time Slots Section */}
          <div className="w-full">
            <TimeSlots
              timeSlots={timeSlots}
              ref={timeSlotsRef}
              selectedDate={selectedDate}
              currentDate={currentDate}
              continue={handleNext}
              onSelectNext={(time) => {
                setSelectedTime(time);
              }}
            />

            {selectedTime && (
              <div className="my-6 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between gap-4">
                  <button
                    onClick={() => {
                      setSelectedTime(null);
                      timeSlotsRef.current.resetTime();
                    }}
                    className="bg-gray-400 text-white rounded-md font-medium hover:bg-gray-500 transition-colors text-sm px-6 py-3"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleNext}
                    className="bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 transition-colors text-sm px-6 py-3"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="justify-end">
          {/* Bottom Section - Fixed at bottom */}
          {/* {selectedTime && (
            <div className="mt-auto pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between gap-4">
                <button
                  onClick={() => {
                    setSelectedTime(null);
                    timeSlotsRef.current.resetTime();
                  }}
                  className="bg-gray-400 text-white rounded-md font-medium hover:bg-gray-500 transition-colors text-sm px-6 py-3"
                >
                  Cancel
                </button>
                <button
                  onClick={handleNext}
                  className="bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 transition-colors text-sm px-6 py-3"
                >
                  Next
                </button>
              </div>
            </div>
          )} */}
        </div>
      </div>
    </div>
  );
};

export default Calendar;
